import { useState, useEffect, useRef } from 'react'
import { JobCard } from '@/components/JobCard'
import { ProfileCompletionDialog } from '@/components/ProfileCompletionDialog'
import { useJobs } from '@/hooks/useJobs'
import { VirtualizedJobList } from '@/components/VirtualizedJobList'
import { OptimizedJobFilters } from '@/components/OptimizedJobFilters'
import { JobListSkeleton } from '@/components/ui/skeleton'
import { useUserProfileWithRole } from '@/hooks/useUserProfileWithRole'
import { useAuth } from '@/contexts/AuthContext'
import { SimpleAnnouncementCard } from '@/components/SimpleAnnouncementCard'
import { useAnnouncements } from '@/hooks/useAnnouncements'
import { PageHeader } from '@/components/common/PageHeader'
import { HeroSection } from '@/components/common/HeroSection'
import { JobFiltersSection } from '@/components/job/JobFiltersSection'
import { JobListingHeader } from '@/components/job/JobListingHeader'
import { UserDropdownMenu } from '@/components/user/UserDropdownMenu'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { Button } from '@/components/ui/button'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from '@/components/ui/pagination'
import { Loader2, Clapperboard } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useToast } from '@/hooks/use-toast'
import SEOHead from '@/components/SEOHead'
import { updateSearchParams } from '@/utils/urlHelpers'
import { useDebounce } from '@/hooks/useDebounce'

const Index = () => {

  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { toast } = useToast()
  const { user, signOut } = useAuth()
  const { profile, role, isLoading: profileLoading } = useUserProfileWithRole()
  const isAdmin = role === 'admin'
  const displayName = profile?.display_name || profile?.name || profile?.email || user?.email || ''
  const { announcements, loading: announcementsLoading } = useAnnouncements()

  // Ref for auto-scroll to job listings
  const jobListingsRef = useRef<HTMLDivElement>(null)

  // Component state
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')
  const [selectedRole, setSelectedRole] = useState(searchParams.get('role') || 'all')
  const [selectedLocation, setSelectedLocation] = useState(searchParams.get('location') || 'all')
  const [selectedProjectType, setSelectedProjectType] = useState(searchParams.get('type') || 'all')
  const [selectedDateFilter, setSelectedDateFilter] = useState('all')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

  const currentPage = parseInt(searchParams.get('page') || '1')

  // Debounced search term for auto-scroll
  const debouncedSearchTerm = useDebounce(searchTerm, 500)

  const { jobs, loading, totalCount, totalPages } = useJobs({
    searchTerm,
    selectedRole,
    selectedLocation,
    selectedProjectType,
    selectedDateFilter,
    currentPage,
  })

  // Fetch data on component mount and user changes
  useEffect(() => {
    // No additional data fetching needed on mount
  }, [user])

  // Update URL search parameters helper
  const handleSearchParamsUpdate = (updates: Record<string, string>) => {
    const newParams = updateSearchParams(searchParams, updates)
    // Reset to page 1 when filters change (except explicit page changes)
    if (!updates.page) {
      newParams.delete('page')
    }
    setSearchParams(newParams)
  }

  // Event handlers
  const clearSearch = () => {
    setSearchTerm('')
    handleSearchParamsUpdate({ search: '' })
  }

  const applyFilters = () => {
    // Filters are applied automatically through the useJobs hook
  }

  const clearAllFilters = () => {
    setSearchTerm('')
    setSelectedRole('all')
    setSelectedLocation('all')
    setSelectedProjectType('all')
    setSelectedDateFilter('all')
    handleSearchParamsUpdate({ search: '', role: 'all', location: 'all', type: 'all' })
  }

  const handlePageChange = (page: number) => {
    handleSearchParamsUpdate({ page: page.toString() })
  }

  const handleLogout = async () => {
    await signOut()
    navigate('/')
  }

  // Auto-scroll to job listings when search term changes
  useEffect(() => {
    if (debouncedSearchTerm && jobListingsRef.current) {
      jobListingsRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [debouncedSearchTerm])

  // Update URL when individual filters change
  useEffect(() => {
    handleSearchParamsUpdate({
      search: searchTerm,
      role: selectedRole,
      location: selectedLocation,
      type: selectedProjectType,
    })
  }, [searchTerm, selectedRole, selectedLocation, selectedProjectType])

  return (
    <>
      <SEOHead
        title="Indie Scene Jobs | Find Indie Film & Creative Gigs"
        description="Remote & local indie film jobs. Connect with creative professionals, filmmakers & studios worldwide."
        keywords="film jobs, video production, indie film, creative jobs, director jobs, cinematographer jobs, editor jobs, actor jobs, film crew, youtube jobs"
        canonical="https://indiescenejobs.com/"
      />
      <div className="min-h-screen bg-background">
        <ProfileCompletionDialog />

        {/* Header */}
        <PageHeader onLogoClick={() => navigate('/')}>
          <button
            onClick={() => navigate('/')}
            className="text-primary font-medium text-sm md:text-base"
          >
            Home
          </button>
          <button
            onClick={() => navigate('/announcements')}
            className="text-muted-foreground hover:text-foreground font-medium text-sm md:text-base"
          >
            Announcements
          </button>
          {user && (
            <Button
              onClick={() => navigate('/submit-job')}
              className="bg-orange hover:bg-orange/90 text-orange-foreground font-medium px-3 py-1.5 text-sm md:px-4 md:py-2"
            >
              Post a Job
            </Button>
          )}
          {user ? (
            <UserDropdownMenu
              user={user}
              userDisplayName={displayName}
              isAdmin={isAdmin}
              onNavigateToProfile={() => navigate('/profile')}
              onNavigateToDashboard={() => navigate('/dashboard')}
              onLogout={handleLogout}
            />
          ) : (
            <Button
              onClick={() => navigate('/auth')}
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-4 py-2 md:px-6 md:py-2 rounded-lg shadow-lg border-2 border-primary/20 text-sm md:text-base"
            >
              Sign Up Free
            </Button>
          )}
        </PageHeader>

        {/* Hero Section */}
        <HeroSection
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onClearSearch={clearSearch}
        />

        {/* Announcements Section */}
        {!announcementsLoading && announcements.length > 0 && (
          <section className="container mx-auto px-4 py-4">
            <h2 className="text-2xl font-bold mb-4">Latest Announcements</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {announcements.slice(0, 3).map((announcement) => (
                <SimpleAnnouncementCard key={announcement.id} announcement={announcement} />
              ))}
            </div>
          </section>
        )}

        {/* Optimized Filters Section */}
        <div className="container mx-auto px-4">
          <OptimizedJobFilters
            selectedRole={selectedRole}
            onRoleChange={setSelectedRole}
            selectedLocation={selectedLocation}
            onLocationChange={setSelectedLocation}
            selectedProjectType={selectedProjectType}
            onProjectTypeChange={setSelectedProjectType}
            selectedDateFilter={selectedDateFilter}
            onDateFilterChange={setSelectedDateFilter}
          />
        </div>

        <main className="container mx-auto px-4 py-8">
          {/* Latest Opportunities Section */}
          <div ref={jobListingsRef} className="mb-8">
            <JobListingHeader
              totalListings={totalCount}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />
          </div>

          {/* Optimized Loading and Job Display */}
          {loading && jobs.length === 0 ? (
            <JobListSkeleton count={12} />
          ) : (
            <VirtualizedJobList 
              jobs={jobs} 
              viewMode={viewMode} 
              isLoading={loading && jobs.length > 0}
            />
          )}

          {/* Helper function to build page URLs */}
          {(() => {
            const buildPageUrl = (page: number) => {
              const newParams = updateSearchParams(searchParams, { page: page.toString() })
              return `/?${newParams.toString()}`
            }

            return (
              <>
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center mt-8">
                    <Pagination>
                      <PaginationContent>
                        {currentPage > 1 && (
                          <PaginationItem>
                            <PaginationPrevious
                              href={buildPageUrl(currentPage - 1)}
                              onClick={(e) => {
                                e.preventDefault()
                                handlePageChange(currentPage - 1)
                              }}
                              className="cursor-pointer"
                            />
                          </PaginationItem>
                        )}

                        {/* Show first page */}
                        {currentPage > 3 && (
                          <>
                            <PaginationItem>
                              <PaginationLink
                                href={buildPageUrl(1)}
                                onClick={(e) => {
                                  e.preventDefault()
                                  handlePageChange(1)
                                }}
                                isActive={currentPage === 1}
                                className="cursor-pointer"
                              >
                                1
                              </PaginationLink>
                            </PaginationItem>
                            {currentPage > 4 && (
                              <PaginationItem>
                                <PaginationEllipsis />
                              </PaginationItem>
                            )}
                          </>
                        )}

                        {/* Show pages around current page */}
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i
                          if (page > totalPages) return null

                          return (
                            <PaginationItem key={page}>
                              <PaginationLink
                                href={buildPageUrl(page)}
                                onClick={(e) => {
                                  e.preventDefault()
                                  handlePageChange(page)
                                }}
                                isActive={currentPage === page}
                                className="cursor-pointer"
                              >
                                {page}
                              </PaginationLink>
                            </PaginationItem>
                          )
                        })}

                        {/* Show last page */}
                        {currentPage < totalPages - 2 && (
                          <>
                            {currentPage < totalPages - 3 && (
                              <PaginationItem>
                                <PaginationEllipsis />
                              </PaginationItem>
                            )}
                            <PaginationItem>
                              <PaginationLink
                                href={buildPageUrl(totalPages)}
                                onClick={(e) => {
                                  e.preventDefault()
                                  handlePageChange(totalPages)
                                }}
                                isActive={currentPage === totalPages}
                                className="cursor-pointer"
                              >
                                {totalPages}
                              </PaginationLink>
                            </PaginationItem>
                          </>
                        )}

                        {currentPage < totalPages && (
                          <PaginationItem>
                            <PaginationNext
                              href={buildPageUrl(currentPage + 1)}
                              onClick={(e) => {
                                e.preventDefault()
                                handlePageChange(currentPage + 1)
                              }}
                              className="cursor-pointer"
                            />
                          </PaginationItem>
                        )}
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </>
            )
          })()}

          {/* Results Summary */}
          {!loading && jobs.length > 0 && (
            <div className="text-center text-sm text-muted-foreground mb-4">
              Showing {(currentPage - 1) * 12 + 1}-{Math.min(currentPage * 12, totalCount)} of{' '}
              {totalCount} jobs
            </div>
          )}

          {/* Empty State */}
          {!loading && jobs.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">No jobs match your current filters.</p>
              <Button variant="outline" onClick={clearAllFilters}>
                Clear Filters
              </Button>
            </div>
          )}
        </main>

        {/* Footer */}
        <footer className="bg-gray-100 border-t mt-16">
          <div className="container mx-auto px-4 py-12">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Column 1 - Brand */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Clapperboard className="w-6 h-6" />
                  <span className="text-xl font-bold">Indie Scene Jobs</span>
                </div>
                <p className="text-muted-foreground">Connecting filmmakers with creative talent.</p>
              </div>

              {/* Column 2 - Explore */}
              <div>
                <h4 className="font-semibold mb-4">Explore</h4>
                <ul className="space-y-2">
                  <li>
                    <button
                      onClick={() => navigate('/')}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      Browse Jobs
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => navigate('/submit-job')}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      Post a Job
                    </button>
                  </li>
                </ul>
              </div>

              {/* Column 3 - Support */}
              <div>
                <h4 className="font-semibold mb-4">Support</h4>
                <ul className="space-y-2">
                  <li>
                    <button
                      onClick={() => navigate('/contact')}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      Contact Us
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => navigate('/help')}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      Help Center
                    </button>
                  </li>
                </ul>
              </div>

              {/* Column 4 - Legal */}
              <div>
                <h4 className="font-semibold mb-4">Legal</h4>
                <ul className="space-y-2">
                  <li>
                    <button
                      onClick={() => navigate('/privacy')}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      Privacy Policy
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => navigate('/terms')}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      Terms of Service
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}

export default Index
